import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, Optional, Inject, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ReportableTransactionService } from './reportable-transaction.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-reportable-transaction',
    templateUrl: './reportable-transaction.component.html',
    styleUrls: ['./reportable-transaction.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReportableTransactionComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-close') requestClose = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Flag to track if opened as dialog
  private isDialogMode: boolean = false;

  /** When opened via MatDialog, row/line context is supplied here (not from activeScreen). */
  private readonly dialogPayload: {
    dataRow?: any;
    rowData?: any;
    repTransaction?: any[];
    isValidScenario?: boolean;
  } | null;

  // Component properties — `globalService` is flat global params for service _.merge compatibility
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;
  filterParams: any = {};

  minimise: boolean = false;
  crudLoading: boolean = false;
  loadingData: boolean = true;
  initialized: boolean = false;

  // Data properties
  dataRow: any;
  rowData: any;
  repTransaction: any[] = [];
  isValidScenario: boolean = false;

  repTransHeader: any = {};
  repTransDetails: any[] = [];
  originalRepHeader: any = {};
  originalRepTransaction: any[] = [];
  deletedRepTransaction: any[] = [];

  ReporatableActions: any[] = [];
  ReporatableReasonTypes: any[] = [];
  AccountList: any[] = [];
  AccountAmtData: any[] = [];

  priorReportableAdjustment: any[] = [{ EARLY_ADJ_AMT: 0 }];
  rep_trans_id: number = 0;
  rpt_tax_amt: number = 0;
  gain_loss_amt: number = 0;
  rep_line_attrib_key: number = 0;

  enableAddRow: boolean = true;
  targetAcct: string = '5555555 - Reportable Transactions';
  targetAdjustmentAmt: number = 0;
  targetCYAmount: number = 0;
  targetAdjCYAmount: number = 0;

  // Error flags
  rptdescError: boolean = false;
  typeofrptError: boolean = false;
  reportabletaxamountError: boolean = false;
  reasonforzeroError: boolean = false;
  ACCT_KEY_ERROR: boolean = false;
  RECLASS_ADJ_AMT_ERROR: boolean = false;

  logged_in_user: string = '';

  dummyRepTransaction: any[] = [];

  constructor(
    private reportableTransactionService: ReportableTransactionService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    @Optional() private dialogRef: MatDialogRef<ReportableTransactionComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: {
      dataRow?: any;
      rowData?: any;
      repTransaction?: any[];
      isValidScenario?: boolean;
    } | null,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.isDialogMode = !!this.dialogRef;
    this.dialogPayload = dialogData || null;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.initIfReady();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        if (!this.isDialogMode) {
          this.initialized = false;
          this.applyWorkspaceContext();
          this.initIfReady();
          this.cdr.markForCheck();
        }
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        if (!this.isDialogMode) {
          this.initialized = false;
          this.applyWorkspaceContext();
          this.initIfReady();
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService = header.globalParams;

    if (this.dialogPayload?.dataRow != null) {
      this.dataRow = this.dialogPayload.dataRow;
      this.rowData = this.dialogPayload.rowData || {};
      this.repTransaction = this.dialogPayload.repTransaction || [];
      this.isValidScenario = !!this.dialogPayload.isValidScenario;
    } else {
      const d = activeScreen.data || {};
      this.dataRow = d.dataRow ?? d.data_row ?? {};
      const rawRow = rowPayloadFromActiveScreen(activeScreen);
      this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
      this.repTransaction = d.repTransaction ?? d.rep_transaction ?? [];
      const ivs = d.isValidScenario ?? d.is_valid_scenario;
      this.isValidScenario =
        typeof ivs === 'boolean' ? ivs : ivs === 'true' || ivs === '1' || ivs === 1;
    }
    this.cdr.markForCheck();
  }

  initIfReady(): void {
    if (this.initialized) return;
    
    if (this.dataRow && Object.keys(this.dataRow).length > 0 && this.rowData && Object.keys(this.rowData).length > 0 && this.GENERAL_CONFIG) {
      this.initialized = true;
      this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
      
      console.log('initIfReady called - isValidScenario:', this.isValidScenario);
      
      this.initializeData();
    }
  }

  initializeData(): void {
    console.log('Initializing Reportable Transaction component');
    console.log('dataRow:', this.dataRow);
    console.log('rowData:', this.rowData);
    console.log('GENERAL_CONFIG:', this.GENERAL_CONFIG);
    console.log('USER_SETTINGS:', this.USER_SETTINGS);
    console.log('logged_in_user (sso_id):', this.logged_in_user);
    console.log('globalService:', this.globalService);

    // Extract rep_trans_id and gain_loss_amt from dataRow
    const attribShortCode = 'REP_TRANS_ID_' + this.dataRow.Line_no;
    const gainLossAttribShortCode = 'GAIN_LOSS_' + this.dataRow.Line_no;
    
    console.log('Looking for attribShortCode:', attribShortCode);
    console.log('Looking for gainLossAttribShortCode:', gainLossAttribShortCode);
    console.log('dataRow.Line_data_occ:', this.dataRow.Line_data_occ);
    
    if (this.dataRow.Line_data_occ) {
      for (let i = 0; i < this.dataRow.Line_data_occ.length; i++) {
        const item = this.dataRow.Line_data_occ[i];
        if (item.ATTRIB_SHORT_CODE === attribShortCode) {
          if (item.ATTRIB_VALUE != null) {
            this.rep_trans_id = parseInt(item.ATTRIB_VALUE) || 0;
          }
          this.rep_line_attrib_key = item.LINE_ATTRIB_KEY;
          console.log('Found rep_trans_id:', this.rep_trans_id, 'rep_line_attrib_key:', this.rep_line_attrib_key);
        } else if (item.ATTRIB_SHORT_CODE === gainLossAttribShortCode && item.ATTRIB_VALUE != null) {
          this.gain_loss_amt = parseFloat(item.ATTRIB_VALUE) || 0;
          console.log('Found gain_loss_amt:', this.gain_loss_amt);
        }
      }
    }
    
    console.log('Final values - rep_trans_id:', this.rep_trans_id, 'gain_loss_amt:', this.gain_loss_amt);

    // Initialize repTransHeader
    this.repTransHeader = {
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      ADJ_SOURCE: '',
      ADJ_TYPE: '',
      ADJ_DESC: '',
      ADJ_ORIGIN_SOURCE: '',
      REP_TAX_AMT: this.rpt_tax_amt,
      HEADER_COMMENTS: '',
      REASON_REPORTABLE: '',
      REPORTABLE_ACTION: '',
      OCCURRENCE: this.dataRow.Occurrence,
      SCHDD_DETAILS_KEY: this.dataRow.Trans_details_key,
      TAX_YEAR: this.rowData.TAX_YEAR,
      SCHDD_LINE_NO: this.dataRow.Line_no,
      REP_LINE_ATTRIB_KEY: this.rep_line_attrib_key,
      IS_CHANGED_FLAG: 'N',
      ISSUE_KEY: this.rowData.ISSUE_KEY
    };

    // Initialize dummy rep transaction
    this.dummyRepTransaction = [{
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      REP_TRANS_AMT: 0,
      CY_AMOUNT: 0,
      EARLY_ADJ_AMT: 0,
      ADJUSTED_CY_AMOUNT: 0,
      ACCT_KEY: null,
      ACCT: '',
      ACCT_DESC: '',
      GL_ACCT_DESC: '',
      OCCURRENCE: 0,
      PARENT_OCCURRENCE: this.dataRow.Occurrence,
      SCHDD_DETAILS_KEY: this.dataRow.Trans_details_key,
      TAX_YEAR: this.rowData.TAX_YEAR,
      IS_CHANGED_FLAG: 'N',
      SCENARIO: this.rowData.SCENARIO_KEY,
      ISSUE_KEY: this.rowData.ISSUE_KEY
    }];

    // Load data
    this.getRepTransHeader();
    this.getRepTransDetails();
    this.getReportableTransTypes();
    this.getReportableTransReasonTypes();
  }

  getReportableTransTypes(): void {
    console.log('Calling getReportableTransTypes (action_code: w1ubsf) with:');
    console.log('  globalService:', this.globalService);
    console.log('  filterParams:', this.filterParams);
    
    this.reportableTransactionService.getReportableTransactionTypes(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Trans Types Response (w1ubsf):', data);
        this.ReporatableActions = data.jsonObject || [];
        console.log('ReporatableActions populated:', this.ReporatableActions);
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading trans types (w1ubsf):', error);
      }
    );
  }

  getReportableTransReasonTypes(): void {
    console.log('Calling getReportableTransReasonTypes (action_code: hg67p4) with:');
    console.log('  globalService:', this.globalService);
    console.log('  filterParams:', this.filterParams);
    
    this.reportableTransactionService.getReportableTransReasonTypes(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Trans Reason Types Response (hg67p4):', data);
        this.ReporatableReasonTypes = data.jsonObject || [];
        console.log('ReporatableReasonTypes populated:', this.ReporatableReasonTypes);
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading trans reason types (hg67p4):', error);
      }
    );
  }

  getRepTransHeader(): void {
    this.reportableTransactionService.getRepTransHeader(
      this.GENERAL_CONFIG.base_url,
      this.dataRow,
      this.rowData,
      this.rep_trans_id,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Rep Trans Header:', data);
        if (data.jsonObject && data.jsonObject.length > 0) {
          this.repTransHeader = data.jsonObject[0];
        }
        this.originalRepHeader = _.cloneDeep(this.repTransHeader);
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading rep trans header:', error);
      }
    );
  }

  getRepTransDetails(): void {
    this.reportableTransactionService.getRepTransDetails(
      this.GENERAL_CONFIG.base_url,
      this.dataRow,
      this.rowData,
      this.rep_trans_id,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Rep Trans Details:', data);
        if (data.jsonObject && data.jsonObject.length > 0) {
          this.repTransDetails = data.jsonObject;
        }
        this.originalRepTransaction = _.cloneDeep(this.repTransDetails);
        
        this.targetAdjustmentAmt = 0;
        for (let i = 0; i < this.repTransDetails.length; i++) {
          this.targetAdjustmentAmt = this.targetAdjustmentAmt - (this.repTransDetails[i].REP_TRANS_AMT || 0);
        }
        
        this.getAccountList();
        this.loadingData = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading rep trans details:', error);
        this.loadingData = false;
        this.cdr.markForCheck();
      }
    );
  }

  getAccountList(): void {
    this.reportableTransactionService.getLocalAccountList(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.rep_trans_id,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Account List:', data);
        this.AccountList = data.jsonObject || [];
        this.getPriorReportableAdjustment();
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading account list:', error);
      }
    );
  }

  getPriorReportableAdjustment(): void {
    this.reportableTransactionService.getPriorReportableAdjustment(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.rep_trans_id,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Prior Reportable Adjustment:', data);
        if (data.jsonObject && data.jsonObject[0] != null && data.jsonObject.length > 0) {
          this.priorReportableAdjustment = data.jsonObject;
        }
        if (this.priorReportableAdjustment[0]?.EARLY_ADJ_AMT != null) {
          this.targetAdjCYAmount = this.targetAdjustmentAmt + this.priorReportableAdjustment[0].EARLY_ADJ_AMT;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading prior reportable adjustment:', error);
      }
    );
  }

  applyTaxacct(): void {
    console.log('Apply button clicked. gain_loss_amt:', this.gain_loss_amt);
    this.repTransHeader.REP_TAX_AMT = this.gain_loss_amt;
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    this.reportabletaxamountError = false;
    console.log('REP_TAX_AMT set to:', this.repTransHeader.REP_TAX_AMT);
    this.cdr.markForCheck();
  }

  reportableactionchange(): void {
    console.log('After changing action:', this.repTransHeader.REPORTABLE_ACTION);
    const selectedAcctIndex = this.ReporatableActions.findIndex(
      x => x.CODE_DESC === this.repTransHeader.REPORTABLE_ACTION
    );
    if (selectedAcctIndex > -1) {
      this.repTransHeader.ADJ_SOURCE = this.ReporatableActions[selectedAcctIndex].CODE_NAME;
    }
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    this.cdr.markForCheck();
  }

  getAccountAmtData(index: number, oldValue: string): void {
    this.ACCT_KEY_ERROR = false;
    this.repTransDetails[index].IS_CHANGED_FLAG = 'Y';
    
    const oldAcctIndex = this.AccountList.findIndex(x => x.GL_ACCT === oldValue);
    const selectedAcctIndex = this.AccountList.findIndex(
      x => x.GL_ACCT === this.repTransDetails[index].GL_ACCT_DESC
    );
    
    if (selectedAcctIndex > -1) {
      this.AccountList[selectedAcctIndex].SELECTEDACCT = 1;
      this.repTransDetails[index].ACCT_KEY = this.AccountList[selectedAcctIndex].GL_ACCT_KEY;
      this.repTransDetails[index].IS_CHANGED_FLAG = 'Y';
    }
    
    if (oldValue != null && oldAcctIndex > -1) {
      this.AccountList[oldAcctIndex].SELECTEDACCT = 0;
    }
    
    this.repTransDetails[index].AMT_LOADING = true;
    this.cdr.markForCheck();
    
    this.reportableTransactionService.getLocalAccountAmtData(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.repTransDetails[index].ACCT_KEY,
      this.rep_trans_id,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Account Amt Data:', data);
        this.AccountAmtData = data.jsonObject || [];
        if (this.AccountAmtData[0]) {
          this.repTransDetails[index].CY_AMOUNT = this.AccountAmtData[0].CY_AMOUNT || 0;
          this.repTransDetails[index].EARLY_ADJ_AMT = this.AccountAmtData[0].EARLY_ADJ_AMT || 0;
        } else {
          this.repTransDetails[index].CY_AMOUNT = 0;
          this.repTransDetails[index].EARLY_ADJ_AMT = 0;
        }
        this.repTransDetails[index].AMT_LOADING = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading account amt data:', error);
        this.repTransDetails[index].AMT_LOADING = false;
        this.cdr.markForCheck();
      }
    );
  }

  addReportable(): void {
    const dummyRecord = _.cloneDeep(this.dummyRepTransaction[0]);
    dummyRecord.OCCURRENCE = this.repTransDetails.length;
    dummyRecord.IS_CHANGED_FLAG = 'Y';
    this.repTransDetails.push(dummyRecord);
    this.isEnableAddRow('A');
    this.cdr.markForCheck();
  }

  removeportable(parentIndex: number): void {
    const deletedAcctIndex = this.AccountList.findIndex(
      x => x.GL_ACCT === this.repTransDetails[parentIndex].GL_ACCT_DESC
    );
    if (deletedAcctIndex > -1) {
      this.AccountList[deletedAcctIndex].SELECTEDACCT = 0;
    }
    
    if (this.repTransDetails[parentIndex].TRANS_DETAILS_KEY != null) {
      this.repTransDetails[parentIndex].IS_CHANGED_FLAG = 'D';
      this.deletedRepTransaction.push(this.repTransDetails[parentIndex]);
    }
    
    this.repTransDetails.splice(parentIndex, 1);
    this.isEnableAddRow('D');
    
    this.targetAdjustmentAmt = 0;
    for (let i = 0; i < this.repTransDetails.length; i++) {
      this.targetAdjustmentAmt = this.targetAdjustmentAmt - (this.repTransDetails[i].REP_TRANS_AMT || 0);
    }
    this.getTargetCYAmount();
    this.cdr.markForCheck();
  }

  isEnableAddRow(param: string): void {
    if (this.AccountList.length <= this.repTransDetails.length) {
      this.enableAddRow = false;
    } else {
      if (param === 'I' && this.AccountList.length === this.repTransDetails.length - 1) {
        const dummyRecord = _.cloneDeep(this.dummyRepTransaction[0]);
        this.repTransDetails.push(dummyRecord);
        this.enableAddRow = false;
      } else if (param === 'I' && this.AccountList.length < this.repTransDetails.length - 1) {
        const dummyRecord = _.cloneDeep(this.dummyRepTransaction[0]);
        this.repTransDetails.push(dummyRecord);
      } else {
        this.enableAddRow = true;
      }
    }
  }

  getTargetCYAmount(): void {
    if (this.priorReportableAdjustment[0]?.EARLY_ADJ_AMT != null) {
      this.targetAdjCYAmount = this.targetAdjustmentAmt + this.priorReportableAdjustment[0].EARLY_ADJ_AMT;
    }
  }

  updateRepType(): void {
    this.typeofrptError = false;
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    const selectedAcctIndex = this.ReporatableReasonTypes.findIndex(
      x => x.CODE_DESC === this.repTransHeader.REASON_REPORTABLE
    );
    if (selectedAcctIndex > -1) {
      this.repTransHeader.ADJ_TYPE = this.ReporatableReasonTypes[selectedAcctIndex].CODE_NAME;
    }
    this.cdr.markForCheck();
  }

  updateAmount(parentIndex: number): void {
    this.RECLASS_ADJ_AMT_ERROR = false;
    this.targetAdjustmentAmt = 0;
    
    this.repTransDetails[parentIndex].ADJUSTED_CY_AMOUNT = 
      (this.repTransDetails[parentIndex].CY_AMOUNT || 0) + 
      (this.repTransDetails[parentIndex].EARLY_ADJ_AMT || 0) + 
      (this.repTransDetails[parentIndex].REP_TRANS_AMT || 0);
    this.repTransDetails[parentIndex].IS_CHANGED_FLAG = 'Y';
    
    for (let i = 0; i < this.repTransDetails.length; i++) {
      this.targetAdjustmentAmt = this.targetAdjustmentAmt - (this.repTransDetails[i].REP_TRANS_AMT || 0);
    }
    this.getTargetCYAmount();
    this.cdr.markForCheck();
  }

  reportabletaxamountchange(): void {
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    this.reportabletaxamountError = false;
    this.cdr.markForCheck();
  }

  updatereasonforZBA(): void {
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    this.reasonforzeroError = false;
    this.cdr.markForCheck();
  }

  updateTransDesc(): void {
    this.repTransHeader.IS_CHANGED_FLAG = 'Y';
    this.rptdescError = false;
    this.cdr.markForCheck();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    
    const finalRepTransHeader = _.cloneDeep(this.repTransHeader);
    let finalRepTransDetails = _.cloneDeep(this.repTransDetails);
    finalRepTransDetails = finalRepTransDetails.concat(this.deletedRepTransaction);
    
    console.log('=== Component Save Debug ===');
    console.log('repTransHeader.ADJ_SOURCE:', finalRepTransHeader.ADJ_SOURCE);
    console.log('repTransDetails (before concat):', this.repTransDetails);
    console.log('deletedRepTransaction:', this.deletedRepTransaction);
    console.log('finalRepTransDetails (after concat):', finalRepTransDetails);
    console.log('finalRepTransDetails length:', finalRepTransDetails.length);
    
    const screenKey = this.globalService?.screen_key || this.filterParams?.screen_key || '';
    
    if (!_.isEqual(finalRepTransHeader, this.originalRepHeader) || !_.isEqual(finalRepTransDetails, this.originalRepTransaction)) {
      const validated = this.validateRepTransData(finalRepTransHeader, finalRepTransDetails);
      if (validated) {
        this.crudLoading = true;
        this.cdr.markForCheck();
        
        this.reportableTransactionService.saveSchddReportableTransaction(
          this.GENERAL_CONFIG.base_url,
          this.rowData,
          this.logged_in_user,
          finalRepTransHeader,
          finalRepTransDetails,
          this.rep_trans_id,
          screenKey,
          this.globalService,
          this.filterParams
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (data: any) => {
            console.log('Save result:', data);
            this.crudLoading = false;
            
            // Match AngularJS pattern: check callSuccess === "1" for success
            if (data.callSuccess !== '1') {
              // Error case
              const errorMsg = data.errorMessage && data.errorMessage !== 'null' 
                ? data.errorMessage 
                : 'Error saving Reportable Transaction';
              this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
              this.cdr.markForCheck();
            } else {
              // Success case - matches AngularJS: AlertService.add then vm.cancel() immediately
              this.gtwSnackbarService.openSnackBar('Reportable Transaction has been updated', 'gtw-snackbar--green');
              
              // Close modal immediately (matching AngularJS behavior)
              this.cancel();
              
              // Show info message after close (snackbar service persists outside the modal)
              setTimeout(() => {
                this.gtwSnackbarService.openSnackBar('Please save the form and re-open to see the saved Reportable Transaction!', 'gtw-snackbar--yellow');
              }, 2000);
            }
          },
          (error: any) => {
            console.error('Save error:', error);
            this.crudLoading = false;
            this.gtwSnackbarService.openSnackBar('Error saving Reportable Transaction', 'gtw-snackbar--red');
            this.cdr.markForCheck();
          }
        );
      }
    } else {
      this.cancel();
    }
  }

  validateRepTransData(repTransHeader: any, repTransDetails: any[]): boolean {
    let passed = true;
    
    if (!repTransHeader.ADJ_DESC || repTransHeader.ADJ_DESC.length <= 0) {
      this.rptdescError = true;
      passed = false;
    }
    if (!repTransHeader.REASON_REPORTABLE || repTransHeader.REASON_REPORTABLE.length <= 0) {
      this.typeofrptError = true;
      passed = false;
    }
    if (repTransHeader.REP_TAX_AMT == null || repTransHeader.REP_TAX_AMT === 0) {
      this.reportabletaxamountError = true;
      passed = false;
    }
    
    if (repTransHeader.ADJ_SOURCE === 'ZBA') {
      if (!repTransHeader.HEADER_COMMENTS || repTransHeader.HEADER_COMMENTS.length <= 0) {
        this.reasonforzeroError = true;
        passed = false;
      }
    } else if (repTransHeader.ADJ_SOURCE === 'BAA') {
      for (let i = 0; i < repTransDetails.length; i++) {
        if (!repTransDetails[i].GL_ACCT_DESC || repTransDetails[i].GL_ACCT_DESC.length <= 0) {
          this.ACCT_KEY_ERROR = true;
          passed = false;
        }
        if (repTransDetails[i].REP_TRANS_AMT == null || repTransDetails[i].REP_TRANS_AMT === 0) {
          this.RECLASS_ADJ_AMT_ERROR = true;
          passed = false;
        }
      }
    }
    
    this.cdr.markForCheck();
    return passed;
  }

  cancel(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('The transaction is still in progress', 'gtw-snackbar--yellow');
      return;
    }
    
    // If opened as MatDialog, close the dialog directly
    if (this.isDialogMode && this.dialogRef) {
      this.dialogRef.close('cancel');
      return;
    }
    
    // For custom element mode - emit close event
    const closeEvent = new CustomEvent('request-close', {
      detail: 'cancel',
      bubbles: true
    });
    
    const hostElement = document.querySelector('gtw-reportable-transaction-ce');
    if (hostElement) {
      hostElement.dispatchEvent(closeEvent);
    }
    
    this.requestClose.emit('cancel');
  }

  // Track by function for ngFor
  trackByIndex(index: number, item: any): number {
    return index;
  }
}
